import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockPaymentService } from '@/services/mock/MockPaymentService';
import { PaymentRecord, PaymentStatus, PaymentMethod } from '@/types/domain/payment-management';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ArrowLeft, Save, Calendar, IndianRupee, Landmark } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { formatInr } from '@/lib/utils';

export const PaymentEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [payment, setPayment] = useState<PaymentRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Form states
  const [bankReferenceNumber, setBankReferenceNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('NEFT');
  const [status, setStatus] = useState<PaymentStatus>('SCHEDULED');
  const [paymentDate, setPaymentDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [amount, setAmount] = useState<number>(0);

  useEffect(() => {
    const loadPayment = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const data = await mockPaymentService.getPaymentById(id);
        if (!data) {
          navigate(ROUTES.PAYMENTS.LIST);
          return;
        }
        setPayment(data);
        setBankReferenceNumber(data.bankReferenceNumber || '');
        setPaymentMethod(data.paymentMethod || 'NEFT');
        setStatus(data.status || 'SCHEDULED');
        setPaymentDate(data.paymentDate ? data.paymentDate.slice(0, 10) : '');
        setDueDate(data.dueDate ? data.dueDate.slice(0, 10) : '');
        setAmount(data.amount || 0);
      } finally {
        setIsLoading(false);
      }
    };

    loadPayment();
  }, [id, navigate]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !payment) return;

    try {
      setIsSaving(true);
      await mockPaymentService.updatePayment(id, {
        bankReferenceNumber,
        paymentMethod,
        status,
        paymentDate: paymentDate ? new Date(paymentDate).toISOString() : payment.paymentDate,
        dueDate: dueDate ? new Date(dueDate).toISOString() : payment.dueDate,
        amount,
      });
      navigate(ROUTES.PAYMENTS.DETAILS(id));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update payment instruction.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Treasury Payment Editor...
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="p-16 text-center space-y-4 font-poppins">
        <h2 className="text-lg font-bold text-foreground">Disbursement Record Not Found</h2>
        <Button size="sm" onClick={() => navigate(ROUTES.PAYMENTS.LIST)} className="font-poppins cursor-pointer">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Payments
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 font-poppins max-w-5xl mx-auto">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.PAYMENTS.DETAILS(id!))}
            className="rounded-xl border-border hover:bg-muted font-poppins cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-primary px-2.5 py-0.5 bg-primary/10 rounded-lg border border-primary/20">
                {payment.paymentReference}
              </span>
              <h1 className="text-xl font-extrabold text-foreground tracking-tight font-poppins">
                Edit Treasury Disbursement Instruction
              </h1>
            </div>
            <p className="text-xs text-foreground-secondary font-medium mt-0.5 font-poppins">
              Beneficiary: <strong className="text-foreground">{payment.vendorName}</strong> • Invoice: <strong className="text-primary font-mono">{payment.invoiceNumber}</strong> • PO: <strong className="text-primary font-mono">{payment.poNumber}</strong>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.PAYMENTS.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-payment-form"
            size="sm"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </div>

      <form id="edit-payment-form" onSubmit={handleSave} className="space-y-6">
        {/* Section 1: Banking Clearing & Settlement Channel */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <Landmark className="h-4 w-4 text-primary" />
              Banking Clearing & Settlement Channel
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Bank UTR / Transaction Reference #
                </label>
                <input
                  type="text"
                  value={bankReferenceNumber}
                  onChange={(e) => setBankReferenceNumber(e.target.value)}
                  placeholder="e.g. UTR-HDFC-992144"
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-mono font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Clearing Settlement Method *
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="NEFT">NEFT (National Electronic Fund Transfer)</option>
                  <option value="RTGS">RTGS (Real Time Gross Settlement)</option>
                  <option value="WIRE_TRANSFER">Wire Transfer (Swift International)</option>
                  <option value="ACH">ACH (Automated Clearing House)</option>
                  <option value="CHECK">Check / Demand Draft</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Disbursement Execution Status *
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as PaymentStatus)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="SCHEDULED">Scheduled in Payment Pool</option>
                  <option value="PROCESSING">Processing at Banking Gateway</option>
                  <option value="PAID">Paid / Fully Cleared</option>
                  <option value="PARTIALLY_PAID">Partially Paid</option>
                  <option value="ON_HOLD">On Hold (Treasury Review)</option>
                  <option value="RECONCILIATION_REQUIRED">Reconciliation Required</option>
                  <option value="FAILED">Failed / Returned</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-primary" /> Disbursement Execution Date *
                </label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-primary" /> Net Contractual Due Date *
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Disbursed Amount (INR ₹) */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <IndianRupee className="h-4 w-4 text-primary" />
              Treasury Disbursed Value (INR ₹)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Disbursed Amount (₹) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-mono font-bold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Current Formatted Amount
                </label>
                <div className="w-full rounded-xl border border-border bg-muted/30 p-2.5 text-xs font-mono font-extrabold text-primary">
                  {formatInr(amount)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(ROUTES.PAYMENTS.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer px-6 shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};
